/**
 * The offer as the source product shows one: what it answers, the three questions a quote exists
 * to answer, who it has been shared with, who has signed, what is being built from it, and the
 * paper it was put on.
 *
 * THE SOURCE PRODUCT'S QUOTE RESPONSE PAGE, seen 2026-10-27. Everything on it that is a fact here
 * was already a service with no screen: release to the customer (quote_releases), the customer's
 * acceptance and the countersignature (commercial_acceptances), the customer PDF
 * (prepared_quote_documents), the plan drafted from the offer (on the terms, since today). Each was
 * reachable by an integrator holding a credential and by nobody sitting in the product -- the
 * same shape the first quote request dialog found, one record further along the chain.
 *
 * THE THREE QUESTIONS ANSWER "NOT YET KNOWN" WITH THE REASON rather than a number that is not one.
 * Theirs reads stock per line and a lead time; "At what price?" is answered from our totals, and
 * the total says "incomplete" while a line has no cost source -- our discipline (UNKNOWN is never
 * 0.00) made into the layout. "Can we make it?" and "By when?" say what would answer them until
 * the lot ledger and a plan's lead time are read here.
 *
 * NOT IMITATED: the agent buttons (their drafting layer), the margin editor on the price card
 * (ours is in the terms), the discussion thread.
 */
import React, {useCallback, useContext, useEffect, useMemo, useState} from "react";
import {Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
        FormControlLabel, Switch, TextField, Typography} from "@mui/material";
import {useNavigate} from "react-router";

import {AppContext} from "../../util/AppContext";
import {makeCresAxios} from "../../util/Axios";
import {Blue3, Grey2, Grey5, Grey6, Grey7, Grey8, Grey17} from "../../theme/styles";
import {FormObjectPicker} from "../Forms/FormObjectPicker";
import {hasPrivilege} from "../../util/SessionContext";

const EDIT_PRIVILEGE = "project:edit";
const RECORD_PRIVILEGE = "project:recordAcceptance";
const COUNTERSIGN_PRIVILEGE = "project:countersign";

type Sourcing = {
    OnHand?: {Quantity?: string | null; UnitOfMeasure?: string | null; Lots?: number; Note?: string | null} | null;
    BestSupplier?: {SupplierName?: string | null; SupplierObjectID?: string | null; LeadTimeDays?: number | null;
                    UnitPrice?: {Amount?: string | null; Currency?: string | null} | null} | null;
    LeadTimeDays?: number | null;
};
type Component = {ItemObjectUSID: string; ObjectID?: string | null; Name?: string | null;
                  QuantityPerUnit?: string | null; UnitOfMeasure?: string | null; Sourcing: Sourcing};
type Procurement = {Components: Component[]; ComponentCount: number; ComponentsWithSupplier: number;
                    ComponentsInStock: number; LongestLeadTimeDays?: number | null; Note?: string | null};
type CostBreakdown = {Currency?: string | null; Material?: string | null; Other?: string | null;
                      Labour?: string | null; Equipment?: string | null; LabourNote?: string | null;
                      EquipmentNote?: string | null;
                      PlanHours?: {NormalHours?: string | null; OvertimeHours?: string | null;
                                   MachineHours?: string | null} | null;
                      Overtime?: string | null; TotalCost?: string | null; Quantity?: string | null;
                      QuantitySource?: string | null; PerUnit?: string | null; PerUnitNote?: string | null};
type Terms = {
    QuoteRequestUSID?: string | null; CustomerObjectUSID?: string | null; Currency?: string | null;
    ValidUntil?: string | null; Notes?: string | null;
    ItemObjectUSID?: string | null; ItemObjectID?: string | null; ItemName?: string | null;
    Procurement?: Procurement | null;
    ListOfBuildPlan?: {BuildPlan?: Plan[] | Plan};
};
type Plan = {BuildPlanRevisionUSID?: string | null; ObjectUSID?: string | null;
             ObjectID?: string | null; Status?: string | null; Quantity?: string | null;
             NeedByDate?: string | null};
type Totals = {Currency?: string | null; Total?: string | null; Subtotal?: string | null;
               Lines?: number; LinesWithNoCostSource?: number; HazardousLines?: number};
type Line = {USID: string; ItemObjectUSID?: string | null; Description?: string | null;
             CostSource?: string | null};
type Request = {USID: string; ObjectID?: string | null; ObjectRevisionUSID?: string | null;
                Subject?: string | null; RequestedProduct?: string | null;
                RequesterName?: string | null; RequesterEmail?: string | null};
type Release = {USID: string; RecipientName?: string | null; RecipientEmail?: string | null;
                ReleasedDatetime?: string | null; ExpiresDatetime?: string | null;
                RevokedDatetime?: string | null; Message?: string | null;
                LastViewedDatetime?: string | null};
type Acceptance = {USID: string; Side: string; PrintedName?: string | null;
                   SignedDatetime?: string | null; WithdrawnDatetime?: string | null;
                   WithdrawnReason?: string | null; Live?: boolean};
type LeadTime = {LeadTimeDays?: number | string | null; PlanEndDate?: string | null;
                 MeetsNeedByDate?: boolean | null; ShiftConfigured?: boolean | null;
                 StepsWithoutHours?: number | null};
type Recipient = {USID: string; Name?: string | null; Email?: string | null;
                  CompanyName?: string | null};

type Props = {
    revisionUSID: string;
    objectUSID?: string | null;
    status?: string | null;
    objectID?: string | null;
    /** Bumped by the host when the lines or terms change elsewhere on the page (the line grid
     *  below), so the three questions re-read rather than going on saying "No lines yet". */
    refreshToken?: number;
};

const listOf = <T,>(held: T[] | T | undefined | null): T[] =>
    Array.isArray(held) ? held : (held ? [held] : []);

function asUtc(value?: string | null): Date | null {
    if (!value) return null;
    const text = String(value).replace(" ", "T");
    const at = new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(text) ? text : `${text}Z`);
    return Number.isNaN(at.getTime()) ? null : at;
}
const whenDate = (value?: string | null) => {
    const at = asUtc(value);
    return at ? at.toLocaleDateString(undefined, {day: "numeric", month: "short", year: "numeric"}) : "—";
};
const whenFull = (value?: string | null) => {
    const at = asUtc(value);
    return at ? at.toLocaleString(undefined, {day: "numeric", month: "short", year: "numeric",
                                               hour: "numeric", minute: "2-digit"}) : "—";
};

function money(amount?: string | null, currency?: string | null, places = 2): string {
    if (amount == null || amount === "") return "—";
    const n = Number(amount);
    if (!Number.isFinite(n)) return String(amount);
    return `${currency ?? ""} ${n.toLocaleString(undefined, {minimumFractionDigits: places,
                                                              maximumFractionDigits: places})}`.trim();
}

/** One of the three answers. `known` colours it; an unknown answer says why, in the same place. */
function Answer({question, answer, detail, known, tone}:
                {question: string; answer: string; detail?: React.ReactNode; known: boolean;
                 tone?: "good" | "warn" | "bad"}) {
    const colour = !known ? Grey6 : tone === "good" ? "#1b7f3b" : tone === "bad" ? "#b3261e"
                 : tone === "warn" ? "#8a6d00" : Grey17;
    const background = !known ? "#fafafa" : tone === "good" ? "#f1faf3" : tone === "bad" ? "#fdf3f2"
                     : tone === "warn" ? "#fffaea" : "white";
    return (
        <Box sx={{flex: "1 1 240px", border: `1px solid ${Grey2}`, borderRadius: "8px",
                  padding: "12px 14px", background}}>
            <Typography sx={{fontSize: 10.5, color: Grey7, textTransform: "uppercase",
                             letterSpacing: "0.05em"}}>{question}</Typography>
            <Typography sx={{fontSize: 20, fontWeight: 700, color: colour, paddingTop: "2px"}}>
                {answer}
            </Typography>
            {detail && <Typography component="div" sx={{fontSize: 12, color: Grey8, paddingTop: "4px",
                                                       lineHeight: 1.5}}>{detail}</Typography>}
        </Box>
    );
}

function Section({title, action, children}:
                 {title: string; action?: React.ReactNode; children: React.ReactNode}) {
    return (
        <Box sx={{border: `1px solid ${Grey2}`, borderRadius: "8px", padding: "12px 16px"}}>
            <Box sx={{display: "flex", alignItems: "center", gap: "10px", paddingBottom: "8px"}}>
                <Typography sx={{fontSize: 14, fontWeight: 600, color: Grey8, flex: 1}}>{title}</Typography>
                {action}
            </Box>
            {children}
        </Box>
    );
}

export default function QuoteOfferPane({revisionUSID, objectUSID, status, objectID, refreshToken}: Props) {
    const appContext: any = useContext(AppContext);
    const api = useMemo(() => makeCresAxios(appContext?.sessionContext), [appContext?.sessionContext]);
    const navigate = useNavigate();

    const [terms, setTerms] = useState<Terms | null>(null);
    const [totals, setTotals] = useState<Totals | null>(null);
    const [breakdown, setBreakdown] = useState<CostBreakdown | null>(null);
    const [lines, setLines] = useState<Line[]>([]);
    const [request, setRequest] = useState<Request | null>(null);
    const [releases, setReleases] = useState<Release[]>([]);
    const [acceptances, setAcceptances] = useState<Acceptance[]>([]);
    const [leadTime, setLeadTime] = useState<LeadTime | null>(null);
    const [granted, setGranted] = useState<string[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const [dialog, setDialog] = useState<"share" | "accept" | "countersign" | null>(null);
    const [rendered, setRendered] = useState<string | null>(null);
    const [linking, setLinking] = useState(false);
    const [ratesOpen, setRatesOpen] = useState(false);

    const reload = useCallback(() => {
        if (!revisionUSID) return;
        api.get("t1/v1/prepared_quote_terms", {params: {PreparedQuoteRevisionUSID: revisionUSID}})
            .then((r: any) => setTerms(r?.data?.PlatformMessage?.PreparedQuoteTerms ?? null))
            .catch(() => setTerms(null));
        api.get("t1/v1/prepared_quote_lines", {params: {PreparedQuoteRevisionUSID: revisionUSID}})
            .then((r: any) => {
                const message = r?.data?.PlatformMessage ?? {};
                setLines(listOf<Line>(message?.ListOfPreparedQuoteLine?.PreparedQuoteLine));
                setTotals(message?.Totals ?? null);
                setBreakdown(message?.CostBreakdown ?? null);
            })
            .catch(() => { setLines([]); setTotals(null); setBreakdown(null); });
        api.get("t1/v1/commercial_acceptances",
                {params: {PreparedQuoteRevisionUSID: revisionUSID, IncludeWithdrawn: "true"}})
            .then((r: any) => setAcceptances(listOf<Acceptance>(
                r?.data?.PlatformMessage?.ListOfCommercialAcceptance?.CommercialAcceptance)))
            .catch(() => setAcceptances([]));
        if (objectUSID) {
            api.get("t1/v1/quote_releases",
                    {params: {PreparedQuoteObjectUSID: objectUSID, IncludeRevoked: "true"}})
                .then((r: any) => setReleases(listOf<Release>(
                    r?.data?.PlatformMessage?.ListOfQuoteRelease?.QuoteRelease)))
                .catch(() => setReleases([]));
        }
        api.get("t1/v1/object_access", {params: {USID: revisionUSID}})
            .then((r: any) => setGranted(r?.data?.EffectivePrivileges ?? []))
            .catch(() => setGranted([]));
    }, [api, revisionUSID, objectUSID]);

    useEffect(() => { reload(); }, [reload, refreshToken]);

    // THE REQUEST IT ANSWERS, read once the terms name it.
    useEffect(() => {
        const usid = terms?.QuoteRequestUSID;
        if (!usid) { setRequest(null); return; }
        let live = true;
        api.get("t1/v1/quote_requests", {params: {USID: usid},
                                         headers: {"idoc-response": "v1.QuoteRequest-001"}})
            .then((r: any) => { if (live) setRequest(r?.data?.PlatformMessage?.QuoteRequest ?? null); })
            .catch(() => { if (live) setRequest(null); });
        return () => { live = false; };
    }, [api, terms?.QuoteRequestUSID]);

    // THE PLAN'S LEAD TIME, when a plan exists: the only honest "by when" there is.
    const plans = useMemo(() => listOf<Plan>(terms?.ListOfBuildPlan?.BuildPlan), [terms]);
    useEffect(() => {
        const first = plans[0]?.BuildPlanRevisionUSID;
        if (!first) { setLeadTime(null); return; }
        let live = true;
        api.get("t1/v1/build_plan_lead_times", {params: {BuildPlanRevisionUSID: first}})
            .then((r: any) => { if (live) setLeadTime(r?.data?.PlatformMessage?.BuildPlanLeadTime ?? null); })
            .catch(() => { if (live) setLeadTime(null); });
        return () => { live = false; };
    }, [api, plans]);

    const isDraft = String(status ?? "").toUpperCase() === "DRAFT";
    const mayEdit = granted.includes(EDIT_PRIVILEGE);
    const mayRecord = granted.includes(RECORD_PRIVILEGE);
    const mayCountersign = granted.includes(COUNTERSIGN_PRIVILEGE);
    const currency = totals?.Currency ?? terms?.Currency ?? null;

    const refuse = (problem: any, fallback: string) =>
        setError(problem?.response?.data?.Cause ?? fallback);

    const act = async (work: () => Promise<any>, fallback: string) => {
        setBusy(true); setError(null);
        try { await work(); reload(); setDialog(null); }
        catch (problem) { refuse(problem, fallback); }
        finally { setBusy(false); }
    };

    const renderPdf = () => act(async () => {
        const r = await api.post("t1/v1/prepared_quote_documents",
                                 {PreparedQuoteRevisionUSID: revisionUSID});
        const made = r?.data?.PlatformMessage?.PreparedQuoteDocument;
        setRendered(made?.Name ? `${made.Name} is attached to this record.` : "The PDF was rendered and attached.");
    }, "The PDF could not be rendered.");

    // ── the three answers ────────────────────────────────────────────────────────────────
    const uncalculated = totals?.LinesWithNoCostSource ?? 0;
    const withItem = lines.filter(one => one.ItemObjectUSID).length;
    const priceKnown = Boolean(totals?.Total) && lines.length > 0 && uncalculated === 0;
    const customerSigned = acceptances.some(one => one.Side === "CUSTOMER" && one.Live !== false
                                                   && !one.WithdrawnDatetime);
    const countersigned = acceptances.some(one => one.Side === "MANUFACTURER" && one.Live !== false
                                                  && !one.WithdrawnDatetime);

    return (
        <Box sx={{display: "flex", flexDirection: "column", gap: "16px", paddingTop: "6px",
                  paddingBottom: "18px"}}>
            {error && <Typography sx={{fontSize: 13, color: "#b3261e"}}>{error}</Typography>}

            {/* WHAT THIS ANSWERS */}
            <Typography sx={{fontSize: 13, color: Grey8}}>
                {request ? <>
                    Answers{" "}
                    <Typography component="span" sx={{fontSize: 13, color: Blue3, cursor: "pointer"}}
                                onClick={() => request.ObjectRevisionUSID
                                    && navigate(`/object/${request.ObjectRevisionUSID}`)}>
                        {request.ObjectID ?? "the request"}
                    </Typography>
                    {": "}{request.Subject || request.RequestedProduct || ""}
                    {request.RequesterName ? ` — from ${request.RequesterName}` : ""}
                    {request.RequesterEmail ? ` (${request.RequesterEmail})` : ""}
                </> : (terms ? "Drafted without a request to answer." : "")}
            </Typography>

            {/* THE LINKED ITEM -- what the offer as a whole is for. The source product's "Linked
                item: Not linked to a catalog item" above its three questions (Ed, 2026-10-28). The
                lines name items individually; this is the one "Can we make it?" is asked about.
                Changed through the Items list screen, like every item pick now. */}
            <Box sx={{display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap"}}>
                <Typography sx={{fontSize: 13, color: Grey7}}>Linked item:</Typography>
                {terms?.ItemObjectUSID ? (
                    <Typography sx={{fontSize: 13, color: Grey17}}>
                        <b>{terms.ItemObjectID ?? ""}</b>{terms.ItemObjectID ? " " : ""}{terms.ItemName ?? "an item that could not be read"}
                    </Typography>
                ) : (
                    <Typography sx={{fontSize: 13, color: Grey6, fontStyle: "italic"}}>Not linked to a catalog item</Typography>
                )}
                {mayEdit && isDraft && !linking && (
                    <Typography sx={{fontSize: 12.5, color: Blue3, cursor: "pointer"}}
                                onClick={() => setLinking(true)}>
                        {terms?.ItemObjectUSID ? "Change" : "Link an item"}
                    </Typography>
                )}
                {mayEdit && isDraft && terms?.ItemObjectUSID && !linking && (
                    <Typography sx={{fontSize: 12.5, color: Grey6, cursor: "pointer"}}
                                onClick={() => act(() => api.put("t1/v1/prepared_quote_terms",
                                    {PreparedQuoteRevisionUSID: revisionUSID, ClearItem: true}),
                                    "The item could not be unlinked.")}>
                        Unlink
                    </Typography>
                )}
                {linking && (
                    <Box sx={{minWidth: 320, display: "flex", alignItems: "center", gap: "6px"}}>
                        <FormObjectPicker label="Linked item" classificationCode="Item" options={[]} many={false}
                                          value={terms?.ItemObjectUSID ?? null}
                                          onChange={next => {
                                              setLinking(false);
                                              if (!next) return;
                                              act(() => api.put("t1/v1/prepared_quote_terms",
                                                  {PreparedQuoteRevisionUSID: revisionUSID, ItemObjectUSID: String(next)}),
                                                  "The item could not be linked.");
                                          }}/>
                        <Typography sx={{fontSize: 12.5, color: Grey6, cursor: "pointer"}}
                                    onClick={() => setLinking(false)}>Cancel</Typography>
                    </Box>
                )}
            </Box>

            {/* THE THREE QUESTIONS */}
            <Box sx={{display: "flex", gap: "12px", flexWrap: "wrap"}}>
                <Answer question="Can we make it?" known={false}
                        answer="Not yet known"
                        detail={!terms?.ItemObjectUSID
                            ? "Link this quote to an item to check its lots and suppliers."
                            : lines.length === 0
                                ? `For ${terms.ItemName ?? "the linked item"}. No lines yet; stock is read per line.`
                                : `${withItem} of ${lines.length} line${lines.length === 1 ? "" : "s"} name a part we hold; on-hand and suppliers are read per line in the grid below.`}/>
                <Answer question="By when?"
                        known={Boolean(leadTime?.PlanEndDate)}
                        answer={leadTime?.PlanEndDate ? whenDate(leadTime.PlanEndDate) : "Not yet known"}
                        tone={leadTime?.MeetsNeedByDate === false ? "bad"
                            : leadTime?.MeetsNeedByDate === true ? "good" : undefined}
                        detail={leadTime?.PlanEndDate
                            ? <>{leadTime.LeadTimeDays != null ? `${leadTime.LeadTimeDays} days from the plan's start` : "From the plan"}
                                {leadTime.MeetsNeedByDate === false ? " · misses the need-by date" : ""}
                                {leadTime.ShiftConfigured === false ? " · no shift configured, so this is a guess" : ""}
                                {leadTime.StepsWithoutHours ? ` · ${leadTime.StepsWithoutHours} step(s) carry no hours` : ""}</>
                            : (plans.length ? "The plan has no computable lead time yet."
                                            : "No build plan has been drafted from this offer; a date comes from one.")}/>
                <Answer question="At what price?"
                        known={priceKnown}
                        tone={priceKnown ? "good" : "warn"}
                        answer={totals?.Total && lines.length ? money(totals.Total, currency) : "Not yet known"}
                        detail={lines.length === 0 ? "No lines yet."
                            : uncalculated > 0
                                ? `Calculated total · incomplete: ${uncalculated} line${uncalculated === 1 ? "" : "s"} uncalculated`
                                : <>
                                    {breakdown?.PerUnit && breakdown.Quantity
                                        ? `${money(String(Number(totals?.Total ?? 0) / Number(breakdown.Quantity)), currency)}/unit × ${breakdown.Quantity} (from the ${breakdown.QuantitySource}) · `
                                        : ""}
                                    {totals?.Lines ?? lines.length} line{lines.length === 1 ? "" : "s"}{totals?.HazardousLines ? ` · ${totals.HazardousLines} hazardous` : ""}{terms?.ValidUntil ? ` · valid until ${whenDate(terms.ValidUntil)}` : ""}
                                  </>}/>
            </Box>

            {/* WHERE THE COST COMES FROM, AND PROCUREMENT -- the source product's two panels under
                its three questions (Ed, 2026-10-28: "Build the cost breakdown and procurement
                panel too"). The breakdown is the service's: material and other lines summed from
                the grid, labour and equipment UNCOSTED with the reason (hours exist, rates do
                not), overtime from the terms. Procurement reads the linked item's bill of
                materials and sources each component the way a line is sourced. */}
            <Box sx={{display: "flex", gap: "12px", flexWrap: "wrap", alignItems: "stretch"}}>
                <Box sx={{flex: "1 1 380px"}}>
                    <Section title="Where the cost comes from"
                             action={hasPrivilege("manufacturing:configure") && (
                                 <Typography sx={{fontSize: 12.5, color: Blue3, cursor: "pointer"}}
                                             onClick={() => setRatesOpen(true)}>Shop rates</Typography>)}>
                        <CostBars breakdown={breakdown} currency={currency} lines={lines.length}/>
                    </Section>
                </Box>
                <Box sx={{flex: "1 1 380px"}}>
                    <Section title="Procurement">
                        <ProcurementPanel terms={terms}/>
                    </Section>
                </Box>
            </Box>

            {/* SHARED WITH THE CUSTOMER */}
            <Section title="Shared with the customer"
                     action={mayEdit && (
                         <Button size="small" variant="outlined" disabled={busy}
                                 sx={{textTransform: "none"}} onClick={() => setDialog("share")}>
                             Share with customer
                         </Button>)}>
                <Typography sx={{fontSize: 12, color: Grey7, paddingBottom: "6px"}}>
                    Shares the commercial offer only, through the customer portal. Internal costing,
                    cost sources and hazards stay private.
                </Typography>
                {releases.length === 0 && <Typography sx={{fontSize: 13, color: Grey6}}>Not shared yet.</Typography>}
                {releases.map(one => (
                    <Box key={one.USID} sx={{display: "flex", gap: "12px", alignItems: "baseline",
                                             padding: "4px 0", flexWrap: "wrap"}}>
                        <Typography sx={{fontSize: 13, color: Grey17, minWidth: 220}}>
                            {one.RecipientName ?? one.RecipientEmail ?? "—"}
                            {one.RecipientName && one.RecipientEmail ? ` · ${one.RecipientEmail}` : ""}
                        </Typography>
                        <Typography sx={{fontSize: 12, color: one.RevokedDatetime ? Grey6 : Grey8}}>
                            {one.RevokedDatetime ? `revoked ${whenFull(one.RevokedDatetime)}`
                                : `shared ${whenFull(one.ReleasedDatetime)}${one.ExpiresDatetime ? `, until ${whenDate(one.ExpiresDatetime)}` : ", no expiry"}${one.LastViewedDatetime ? ` · viewed ${whenFull(one.LastViewedDatetime)}` : " · not viewed yet"}`}
                        </Typography>
                        {!one.RevokedDatetime && mayEdit && (
                            <Typography sx={{fontSize: 12, color: Blue3, cursor: "pointer"}}
                                        onClick={() => act(() => api.delete("t1/v1/quote_releases",
                                            {params: {USID: one.USID, Reason: "Revoked from the offer page"}}),
                                            "The share could not be revoked.")}>
                                Revoke
                            </Typography>
                        )}
                    </Box>
                ))}
            </Section>

            {/* SIGNATURES */}
            <Section title="Signatures">
                <Box sx={{display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px"}}>
                    {([["CUSTOMER", "Client / customer", customerSigned, mayRecord, "accept", "Record the customer's acceptance"],
                       ["MANUFACTURER", "Authorised signatory", countersigned, mayCountersign, "countersign", "Countersign"]] as const)
                      .map(([side, heading, signed, may, which, verb]) => {
                        const rows = acceptances.filter(one => one.Side === side);
                        return <Box key={side}>
                            <Typography sx={{fontSize: 10.5, color: Grey7, textTransform: "uppercase",
                                             letterSpacing: "0.05em"}}>{heading}</Typography>
                            {rows.length === 0 && <Typography sx={{fontSize: 13, color: Grey6, fontStyle: "italic",
                                                                   paddingTop: "4px"}}>Not yet signed</Typography>}
                            {rows.map(one => (
                                <Typography key={one.USID} sx={{fontSize: 13, paddingTop: "4px",
                                                                color: one.WithdrawnDatetime ? Grey6 : Grey17,
                                                                textDecoration: one.WithdrawnDatetime ? "line-through" : "none"}}>
                                    {one.PrintedName ?? "—"} · {whenFull(one.SignedDatetime)}
                                    {one.WithdrawnDatetime ? ` · withdrawn${one.WithdrawnReason ? `: ${one.WithdrawnReason}` : ""}` : ""}
                                    {!one.WithdrawnDatetime && mayCountersign && (
                                        <Typography component="span" sx={{fontSize: 12, color: Blue3, cursor: "pointer",
                                                                          paddingLeft: "8px"}}
                                                    onClick={() => act(() => api.delete("t1/v1/commercial_acceptances",
                                                        {params: {USID: one.USID, Reason: "Withdrawn from the offer page"}}),
                                                        "The signature could not be withdrawn.")}>
                                            Withdraw
                                        </Typography>
                                    )}
                                </Typography>
                            ))}
                            {!signed && may && (
                                <Button size="small" variant="outlined" disabled={busy}
                                        sx={{textTransform: "none", marginTop: "8px"}}
                                        onClick={() => setDialog(which)}>{verb}</Button>
                            )}
                        </Box>;
                    })}
                </Box>
                {customerSigned && countersigned && (
                    <Typography sx={{fontSize: 12.5, color: "#1b7f3b", paddingTop: "10px"}}>
                        Both sides have signed: this offer is a deal.
                    </Typography>
                )}
            </Section>

            {/* BUILD PLAN */}
            <Section title="Build plan">
                {plans.length === 0 && (
                    <Typography sx={{fontSize: 13, color: Grey6}}>
                        No build plan has been drafted from this offer. One is drafted from the
                        project's Build Plans tab and names this offer's revision.
                    </Typography>
                )}
                {plans.map(plan => (
                    <Box key={plan.BuildPlanRevisionUSID ?? plan.ObjectID ?? ""}
                         sx={{display: "flex", gap: "12px", alignItems: "baseline", padding: "4px 0"}}>
                        <Typography sx={{fontSize: 13, fontWeight: 700, color: Blue3, cursor: "pointer"}}
                                    onClick={() => plan.BuildPlanRevisionUSID
                                        && navigate(`/object/${plan.BuildPlanRevisionUSID}`)}>
                            {plan.ObjectID ?? "—"}
                        </Typography>
                        <Typography sx={{fontSize: 12.5, color: Grey8}}>
                            {plan.Quantity ? `Qty ${plan.Quantity}` : ""}{plan.NeedByDate ? ` · by ${whenDate(plan.NeedByDate)}` : ""}
                            {plan.Status ? ` · ${String(plan.Status).toLowerCase()}` : ""}
                        </Typography>
                    </Box>
                ))}
            </Section>

            {/* DOCUMENTS */}
            <Section title="Customer document"
                     action={mayEdit && (
                         <Button size="small" variant="outlined" disabled={busy}
                                 sx={{textTransform: "none"}} onClick={renderPdf}>
                             Generate customer PDF
                         </Button>)}>
                <Typography sx={{fontSize: 12.5, color: rendered ? Grey17 : Grey6}}>
                    {rendered ?? "Renders the offer through the tenant's quote template and attaches the PDF to this record, under Attachments. Rendering discloses nothing; sharing does."}
                </Typography>
            </Section>

            {terms?.Notes && (
                <Section title="Internal notes">
                    <Typography sx={{fontSize: 13, color: Grey17, whiteSpace: "pre-wrap"}}>{terms.Notes}</Typography>
                </Section>
            )}

            {ratesOpen && (
                <ShopRatesDialog api={api} onClose={() => setRatesOpen(false)}
                                 onSaved={() => { setRatesOpen(false); reload(); }}/>
            )}
            <ShareDialog open={dialog === "share"} busy={busy} api={api}
                         onClose={() => setDialog(null)}
                         onShare={(body) => act(() => api.post("t1/v1/quote_releases",
                             {PreparedQuoteRevisionUSID: revisionUSID, ...body}),
                             "The offer could not be shared.")}/>
            <SignDialog open={dialog === "accept" || dialog === "countersign"} busy={busy} api={api}
                        side={dialog === "accept" ? "CUSTOMER" : "MANUFACTURER"}
                        onClose={() => setDialog(null)}
                        onSign={(body) => act(() => api.post("t1/v1/commercial_acceptances",
                            {PreparedQuoteRevisionUSID: revisionUSID, ...body}),
                            "The signature could not be recorded.")}/>
            {isDraft && <Typography sx={{fontSize: 11.5, color: Grey5}}>
                {objectID ?? "This offer"} is a draft. Sharing and signing act on the revision as it stands.
            </Typography>}
        </Box>
    );
}

/**
 * The cost by kind, as bars against the total cost. A kind that is not costed draws no bar and
 * says why, in the place a number would otherwise invite a wrong reading of "zero".
 */
function CostBars({breakdown, currency, lines}: {breakdown: CostBreakdown | null; currency: string | null;
                                                   lines: number}) {
    if (!breakdown) {
        return <Typography sx={{fontSize: 13, color: Grey6}}>
            {lines === 0 ? "No lines yet. Costs are read from the lines below once there are some."
                         : "The cost breakdown could not be read."}
        </Typography>;
    }
    const total = Number(breakdown.TotalCost ?? 0);
    const rows: {label: string; amount?: string | null; note?: string | null}[] = [
        {label: "Material", amount: breakdown.Material},
        {label: "Other lines", amount: breakdown.Other, note: "labour, freight, tooling or one-offs quoted as lines"},
        {label: "People / labour", amount: breakdown.Labour, note: breakdown.LabourNote},
        {label: "Equipment", amount: breakdown.Equipment, note: breakdown.EquipmentNote ?? breakdown.LabourNote},
        {label: "Overtime", amount: breakdown.Overtime, note: breakdown.Overtime == null ? "No overtime on the terms." : null},
    ];
    return (
        <Box sx={{display: "flex", flexDirection: "column", gap: "8px"}}>
            {rows.map(row => {
                const amount = row.amount != null ? Number(row.amount) : null;
                const share = amount != null && total > 0 ? Math.max(0, Math.min(1, amount / total)) : 0;
                return (
                    <Box key={row.label}>
                        <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "baseline"}}>
                            <Typography sx={{fontSize: 12.5, color: Grey8}}>{row.label}</Typography>
                            <Typography sx={{fontSize: 13, color: amount != null ? Grey17 : Grey6,
                                             fontVariantNumeric: "tabular-nums"}}
                                        title={row.note ?? undefined}>
                                {amount != null ? money(row.amount, null) : "not costed"}
                            </Typography>
                        </Box>
                        <Box sx={{height: 4, borderRadius: 2, background: "#EEF1F5", marginTop: "3px"}}>
                            <Box sx={{height: 4, borderRadius: 2, width: `${Math.round(share * 100)}%`,
                                      background: amount != null ? Blue3 : "transparent"}}/>
                        </Box>
                        {row.note && (amount == null || row.label === "People / labour" || row.label === "Equipment") && (
                            <Typography sx={{fontSize: 11, color: Grey6, marginTop: "2px"}}>{row.note}</Typography>
                        )}
                    </Box>
                );
            })}
            <Box sx={{borderTop: `1px solid ${Grey2}`, marginTop: "4px", paddingTop: "8px",
                      display: "flex", justifyContent: "space-between", alignItems: "baseline"}}>
                <Box>
                    <Typography sx={{fontSize: 13, fontWeight: 700, color: Grey17}}>Total cost</Typography>
                    <Typography sx={{fontSize: 11, color: Grey6}}>Per unit</Typography>
                </Box>
                <Box sx={{textAlign: "right"}}>
                    <Typography sx={{fontSize: 14, fontWeight: 700, color: Grey17, fontVariantNumeric: "tabular-nums"}}>
                        {money(breakdown.TotalCost, currency)}
                    </Typography>
                    <Typography sx={{fontSize: 11, color: breakdown.PerUnit ? Grey8 : Grey6}}
                                title={breakdown.PerUnitNote ?? undefined}>
                        {breakdown.PerUnit
                            ? `${money(breakdown.PerUnit, currency)} × ${breakdown.Quantity} (${breakdown.QuantitySource})`
                            : (breakdown.PerUnitNote ?? "—")}
                    </Typography>
                </Box>
            </Box>
            <Typography sx={{fontSize: 11, color: Grey6}}>
                Before margin. The price card above adds the margin from the terms.
            </Typography>
        </Box>
    );
}

/**
 * THE SHOP RATES: what an hour of labour and an hour of a machine cost, with the currency they are
 * in, on the tenant's one shift configuration beside the overtime multiplier they combine with.
 * Edited here, where "not costed" is read, by whoever holds manufacturing:configure. An empty
 * rate is left unset -- "not costed" -- never written as zero.
 */
function ShopRatesDialog({api, onClose, onSaved}: {api: any; onClose: () => void; onSaved: () => void}) {
    const [labour, setLabour] = useState("");
    const [equipment, setEquipment] = useState("");
    const [currencyCode, setCurrencyCode] = useState("");
    const [multiplier, setMultiplier] = useState("");
    const [loaded, setLoaded] = useState(false);
    const [busy, setBusy] = useState(false);
    const [problem, setProblem] = useState<string | null>(null);

    useEffect(() => {
        api.get("t1/v1/shift_configuration")
            .then((r: any) => {
                const m = r?.data?.PlatformMessage ?? {};
                setLabour(m.LabourRatePerHour ?? ""); setEquipment(m.EquipmentRatePerHour ?? "");
                setCurrencyCode(m.RateCurrency ?? ""); setMultiplier(m.OvertimeRateMultiplier ?? "");
                setLoaded(true);
            })
            .catch(() => { setLoaded(true); setProblem("The shop rates could not be read."); });
    }, [api]);

    const save = async () => {
        setBusy(true); setProblem(null);
        try {
            await api.put("t1/v1/shift_configuration", {
                LabourRatePerHour: labour.trim() || undefined,
                EquipmentRatePerHour: equipment.trim() || undefined,
                RateCurrency: currencyCode.trim() || undefined,
                OvertimeRateMultiplier: multiplier.trim() || undefined,
                ClearLabourRate: labour.trim() === "" ? true : undefined,
                ClearEquipmentRate: equipment.trim() === "" ? true : undefined,
            });
            onSaved();
        } catch (failure: any) {
            setProblem(failure?.response?.data?.Cause ?? "The shop rates could not be saved.");
            setBusy(false);
        }
    };

    return (
        <Dialog open onClose={onClose} fullWidth maxWidth="xs">
            <DialogTitle sx={{fontSize: 16}}>Shop rates</DialogTitle>
            <DialogContent>
                <Typography sx={{fontSize: 12.5, color: Grey7, paddingBottom: "12px", lineHeight: 1.5}}>
                    What an hour of labour and an hour on a machine cost, for every quote in this
                    account. A rate left empty stays "not costed"; it is never taken as zero.
                </Typography>
                <Box sx={{display: "flex", flexDirection: "column", gap: "12px"}}>
                    <TextField label="Labour rate per hour" size="small" value={labour} disabled={!loaded}
                               onChange={e => setLabour(e.target.value)}/>
                    <TextField label="Equipment rate per hour" size="small" value={equipment} disabled={!loaded}
                               onChange={e => setEquipment(e.target.value)}/>
                    <TextField label="Currency" size="small" value={currencyCode} disabled={!loaded}
                               onChange={e => setCurrencyCode(e.target.value.toUpperCase())}
                               helperText="Three-letter code. Quotes in another currency are not costed against these."/>
                    <TextField label="Overtime multiplier" size="small" value={multiplier} disabled={!loaded}
                               onChange={e => setMultiplier(e.target.value)}
                               helperText="What an overtime hour costs relative to a normal one."/>
                    {problem && <Typography sx={{fontSize: 12.5, color: "#B3261E"}}>{problem}</Typography>}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button size="small" onClick={onClose} sx={{textTransform: "none"}}>Cancel</Button>
                <Button size="small" variant="contained" disableElevation disabled={busy || !loaded}
                        onClick={save} sx={{textTransform: "none"}}>Save</Button>
            </DialogActions>
        </Dialog>
    );
}

/** The linked item's bill of materials, sourced: what each component needs, what we hold, who
 *  would supply it and how long that takes, and the longest of those lead times. */
function ProcurementPanel({terms}: {terms: Terms | null}) {
    const timing = terms?.Procurement ?? null;
    if (!terms?.ItemObjectUSID) {
        return <Typography sx={{fontSize: 13, color: Grey6}}>
            No BOM lead-time analysis yet. Link this quote to an item with a bill of materials to
            see procurement timing.
        </Typography>;
    }
    if (!timing || timing.Note) {
        return <Typography sx={{fontSize: 13, color: Grey6}}>
            {timing?.Note ?? "Procurement timing could not be read."}
        </Typography>;
    }
    const cell: React.CSSProperties = {fontSize: 12.5, color: Grey17, whiteSpace: "nowrap",
                                       overflow: "hidden", textOverflow: "ellipsis"};
    const head: React.CSSProperties = {fontSize: 10.5, color: Grey7, textTransform: "uppercase",
                                       letterSpacing: "0.04em"};
    const columns = "minmax(140px, 2fr) 70px 90px minmax(100px, 1.4fr) 70px";
    return (
        <Box sx={{display: "flex", flexDirection: "column", gap: "8px"}}>
            <Typography sx={{fontSize: 13, color: Grey17}}>
                {timing.LongestLeadTimeDays != null
                    ? <>Longest component lead time: <b>{timing.LongestLeadTimeDays} days</b>.</>
                    : <>No component has a supplier lead time yet.</>}
                {" "}{timing.ComponentsWithSupplier} of {timing.ComponentCount} component{timing.ComponentCount === 1 ? "" : "s"} have an approved supplier; {timing.ComponentsInStock} in stock.
            </Typography>
            <Box sx={{display: "grid", gridTemplateColumns: columns, gap: "8px", borderBottom: `1px solid ${Grey2}`, paddingBottom: "4px"}}>
                <Typography sx={head}>Component</Typography>
                <Typography sx={{...head, textAlign: "right"}}>Per unit</Typography>
                <Typography sx={{...head, textAlign: "right"}}>On-hand</Typography>
                <Typography sx={head}>Best supplier</Typography>
                <Typography sx={{...head, textAlign: "right"}}>Lead time</Typography>
            </Box>
            {timing.Components.map(one => {
                const onHand = one.Sourcing?.OnHand ?? null;
                const best = one.Sourcing?.BestSupplier ?? null;
                const lead = one.Sourcing?.LeadTimeDays ?? best?.LeadTimeDays ?? null;
                return (
                    <Box key={one.ItemObjectUSID} sx={{display: "grid", gridTemplateColumns: columns, gap: "8px", alignItems: "baseline"}}>
                        <Typography sx={cell} title={one.Name ?? ""}>
                            <b>{one.ObjectID ?? ""}</b>{one.ObjectID ? " " : ""}{one.Name ?? "—"}
                        </Typography>
                        <Typography sx={{...cell, textAlign: "right"}}>
                            {one.QuantityPerUnit ?? "—"}{one.UnitOfMeasure ? ` ${one.UnitOfMeasure}` : ""}
                        </Typography>
                        <Typography sx={{...cell, textAlign: "right", color: onHand?.Quantity != null ? Grey17 : Grey6}}
                                    title={onHand?.Note ?? undefined}>
                            {onHand?.Quantity != null ? `${onHand.Quantity}${onHand.UnitOfMeasure ? ` ${onHand.UnitOfMeasure}` : ""}` : "—"}
                        </Typography>
                        <Typography sx={{...cell, color: best ? Grey17 : Grey6}}
                                    title={best ? undefined : "No approved supplier."}>
                            {best?.SupplierName ?? best?.SupplierObjectID ?? "—"}
                        </Typography>
                        <Typography sx={{...cell, textAlign: "right", color: lead != null ? Grey17 : Grey6}}>
                            {lead != null ? `${lead} d` : "—"}
                        </Typography>
                    </Box>
                );
            })}
        </Box>
    );
}

/** Who at the customer: a contact of this account, found by name or address. */
function useRecipients(api: any, open: boolean, search: string) {
    const [found, setFound] = useState<Recipient[]>([]);
    useEffect(() => {
        if (!open) return;
        let live = true;
        api.get("t1/v1/lot_release_recipients", {params: {Search: search || undefined}})
            .then((r: any) => { if (live) setFound(listOf<Recipient>(
                r?.data?.PlatformMessage?.ListOfLotReleaseRecipient?.LotReleaseRecipient
                ?? r?.data?.PlatformMessage?.ListOfRecipient?.Recipient)); })
            .catch(() => { if (live) setFound([]); });
        return () => { live = false; };
    }, [api, open, search]);
    return found;
}

function ShareDialog({open, busy, api, onClose, onShare}:
                     {open: boolean; busy: boolean; api: any; onClose: () => void;
                      onShare: (body: Record<string, any>) => void}) {
    const [email, setEmail] = useState("");
    const [message, setMessage] = useState("");
    const [never, setNever] = useState(false);
    const [expires, setExpires] = useState("");
    const found = useRecipients(api, open, email);
    useEffect(() => { if (open) { setEmail(""); setMessage(""); setNever(false); setExpires(""); } }, [open]);
    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle sx={{fontSize: 16}}>Share this offer with the customer</DialogTitle>
            <DialogContent sx={{display: "flex", flexDirection: "column", gap: "14px", paddingTop: "8px !important"}}>
                <Typography sx={{fontSize: 12.5, color: Grey7, lineHeight: 1.6}}>
                    The contact sees the commercial offer in the customer portal and can accept it
                    there. Costing, cost sources and hazards are not shared.
                </Typography>
                <TextField autoFocus size="small" label="Contact's email" value={email}
                           helperText="A contact of this account. A typo does not create one."
                           onChange={event => setEmail(event.target.value)}/>
                {found.length > 0 && email && (
                    <Box sx={{display: "flex", flexDirection: "column", gap: "2px"}}>
                        {found.slice(0, 6).map(one => (
                            <Typography key={one.USID} sx={{fontSize: 12.5, color: Blue3, cursor: "pointer"}}
                                        onClick={() => setEmail(one.Email ?? "")}>
                                {one.Name ?? one.Email}{one.CompanyName ? ` · ${one.CompanyName}` : ""}{one.Email ? ` · ${one.Email}` : ""}
                            </Typography>
                        ))}
                    </Box>
                )}
                <TextField size="small" label="Message" multiline minRows={2} value={message}
                           onChange={event => setMessage(event.target.value)}/>
                <FormControlLabel control={<Switch size="small" checked={never} onChange={(_, on) => setNever(on)}/>}
                                  label={<Typography sx={{fontSize: 13}}>Never expires</Typography>}/>
                {!never && <TextField size="small" type="date" label="Expires" value={expires}
                                      slotProps={{inputLabel: {shrink: true}}}
                                      onChange={event => setExpires(event.target.value)}/>}
            </DialogContent>
            <DialogActions>
                <Button size="small" onClick={onClose} sx={{textTransform: "none"}}>Cancel</Button>
                <Button size="small" variant="contained" disableElevation disabled={busy || !email.trim()}
                        sx={{textTransform: "none"}}
                        onClick={() => onShare({RecipientEmail: email.trim(), Message: message.trim() || undefined,
                                                NeverExpires: never || undefined,
                                                ExpiresDatetime: !never && expires ? expires : undefined})}>
                    Share
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function SignDialog({open, busy, api, side, onClose, onSign}:
                    {open: boolean; busy: boolean; api: any; side: "CUSTOMER" | "MANUFACTURER";
                     onClose: () => void; onSign: (body: Record<string, any>) => void}) {
    const [name, setName] = useState("");
    const [search, setSearch] = useState("");
    const [contact, setContact] = useState<Recipient | null>(null);
    const found = useRecipients(api, open && side === "CUSTOMER", search);
    useEffect(() => { if (open) { setName(""); setSearch(""); setContact(null); } }, [open]);
    const customer = side === "CUSTOMER";
    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle sx={{fontSize: 16}}>
                {customer ? "Record the customer's acceptance" : "Countersign this offer"}
            </DialogTitle>
            <DialogContent sx={{display: "flex", flexDirection: "column", gap: "14px", paddingTop: "8px !important"}}>
                <Typography sx={{fontSize: 12.5, color: Grey7, lineHeight: 1.6}}>
                    {customer
                        ? "A staff member entering a decision that was the customer's: which contact agreed, and the name they gave. Their own acceptance through the portal records itself."
                        : "Commits the company to this offer as it stands. The signer is you; nobody can sign as somebody else."}
                </Typography>
                {customer && <>
                    <TextField autoFocus size="small" label="Which contact" value={search}
                               helperText={contact ? `Chosen: ${contact.Name ?? contact.Email}` : "Find a contact of this account by name or address."}
                               onChange={event => { setSearch(event.target.value); setContact(null); }}/>
                    {found.length > 0 && !contact && (
                        <Box sx={{display: "flex", flexDirection: "column", gap: "2px"}}>
                            {found.slice(0, 6).map(one => (
                                <Typography key={one.USID} sx={{fontSize: 12.5, color: Blue3, cursor: "pointer"}}
                                            onClick={() => { setContact(one); setSearch(one.Name ?? one.Email ?? "");
                                                             if (!name) setName(one.Name ?? ""); }}>
                                    {one.Name ?? one.Email}{one.CompanyName ? ` · ${one.CompanyName}` : ""}{one.Email ? ` · ${one.Email}` : ""}
                                </Typography>
                            ))}
                        </Box>
                    )}
                </>}
                <TextField size="small" label="Printed name" value={name} autoFocus={!customer}
                           onChange={event => setName(event.target.value)}/>
            </DialogContent>
            <DialogActions>
                <Button size="small" onClick={onClose} sx={{textTransform: "none"}}>Cancel</Button>
                <Button size="small" variant="contained" disableElevation
                        disabled={busy || !name.trim() || (customer && !contact)}
                        sx={{textTransform: "none"}}
                        onClick={() => onSign(customer
                            ? {Side: "CUSTOMER", CustomerExternalUserUSID: contact?.USID, PrintedName: name.trim()}
                            : {Side: "MANUFACTURER", PrintedName: name.trim()})}>
                    {customer ? "Record acceptance" : "Countersign"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
